/**
 * NextRoundHandshake — ordinary Round End exit handshake (Phase 1).
 *
 * Owns the pending next-round transition between the server's
 * next_game_created broadcast and the moment GameScene may tear down the
 * finished board:
 *
 * - begin()       arms a fallback timer, then dispatches the overlay-exit
 *                 signal (phaser:next-round-ready). The finished scene stays
 *                 alive and rendered behind the overlay.
 * - acknowledge() React confirmed the overlay exit (react:round-end-exited);
 *                 runs the transition iff the gameId matches the pending one.
 * - fallback      if React never acknowledges (overlay unmounted, listener
 *                 dropped), the timer runs the same transition so a round can
 *                 never hang on the UI. The server remains the only
 *                 advancement authority — this only defers the LOCAL restart.
 * - cancel()      teardown paths (scene cleanup / re-init); nothing fires
 *                 afterwards.
 *
 * The transition callback is consumed EXACTLY once, regardless of duplicate
 * acknowledgments, an acknowledgment arriving after the fallback fired, a
 * stale/wrong-game acknowledgment, or cancel() racing the timer.
 *
 * Phaser-free by design so its semantics are unit-testable (the GameScene
 * wiring is pinned separately by source-level tests, per the precedent in
 * gameSceneRoundTransition.test.ts).
 */

/** Upper bound on waiting for React's exit acknowledgment. Phase 1 acks in the
 *  same event turn; the visual phase's canonical 180 ms exit animation still
 *  fits with wide margin. Not a presentation duration — purely a failsafe. */
export const NEXT_ROUND_ACK_FALLBACK_MS = 700;

interface PendingTransition {
  gameId: number;
  run: () => void;
}

export class NextRoundHandshake {
  private pending: PendingTransition | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly fallbackMs: number = NEXT_ROUND_ACK_FALLBACK_MS) {}

  /** True while a transition is armed and neither ack nor fallback consumed it. */
  get isPending(): boolean {
    return this.pending !== null;
  }

  /**
   * Arm the transition for `gameId`, then emit the exit signal. A begin() while
   * another transition is pending is a no-op (the duplicate-event guards in
   * GameScene make this unreachable, but the machine stays safe regardless).
   *
   * The fallback timer is armed BEFORE dispatchExitSignal runs: the signal can
   * be acknowledged synchronously inside the dispatch (Phase 1 React acks
   * immediately), and that ack path must find the armed timer to clear.
   */
  begin(gameId: number, dispatchExitSignal: () => void, runTransition: () => void): void {
    if (this.pending) return;
    this.pending = { gameId, run: runTransition };
    this.timer = setTimeout(() => {
      this.timer = null;
      this.consume(gameId);
    }, this.fallbackMs);
    dispatchExitSignal();
  }

  /**
   * React's exit acknowledgment. gameId must echo the pending transition's
   * NEW game id — a stale ack for a different game does nothing (the fallback
   * timer still guarantees progress for the real pending transition).
   */
  acknowledge(gameId: number): void {
    this.consume(gameId);
  }

  /** Teardown: drop any pending transition and disarm the fallback. */
  cancel(): void {
    this.clearTimer();
    this.pending = null;
  }

  private consume(gameId: number): void {
    const pending = this.pending;
    if (!pending || pending.gameId !== gameId) return;
    this.pending = null;
    this.clearTimer();
    pending.run();
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
